import React, { useState, useMemo } from 'react';
import { Calendar, Clock, AlertTriangle, CheckCircle, DollarSign, Info, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { getUnitAvailability, checkBookingConflicts } from '../../utils/enhancedAvailabilityUtils';

export const SmartAvailabilityCalendar = ({
    unitId,
    unitNumber,
    selectedCheckIn,
    selectedCheckOut,
    onDateChange,
    excludeBookingId
}) => {
    const { t } = useTranslation();
    const [hoveredDate, setHoveredDate] = useState(null);
    const [showConflicts, setShowConflicts] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(() => {
        return moment().startOf('month');
    });

    // Generate calendar for current month only
    const calendarDates = useMemo(() => {
        const dates = [];
        const daysInMonth = currentMonth.daysInMonth();

        // Generate all dates in the month
        for (let day = 1; day <= daysInMonth; day++) {
            const date = moment(currentMonth).date(day);
            dates.push(date.format('YYYY-MM-DD'));
        }

        return dates;
    }, [currentMonth]);

    // Get availability for all calendar dates
    const availability = useMemo(() => {
        if (!unitId) return {};

        const startDate = calendarDates[0];
        const endDate = calendarDates[calendarDates.length - 1];

        try {
            const unitAvailability = getUnitAvailability(unitId, startDate, endDate, excludeBookingId);
            const availabilityMap = {};

            unitAvailability.availabilitySlots.forEach(slot => {
                availabilityMap[slot.date] = slot;
            });

            return availabilityMap;
        } catch (error) {
            console.error('Error getting unit availability:', error);
            return {};
        }
    }, [unitId, calendarDates, excludeBookingId]);

    // Navigation functions
    const navigateMonth = (direction) => {
        setCurrentMonth(prev => {
            const newMonth = moment(prev);
            if (direction === 'next') {
                newMonth.add(1, 'month');
            } else {
                newMonth.subtract(1, 'month');
                // Don't allow going to past months
                const currentMonthStart = moment().startOf('month');
                if (newMonth.isBefore(currentMonthStart)) {
                    return prev;
                }
            }
            return newMonth;
        });
    };

    const goToToday = () => {
        setCurrentMonth(moment().startOf('month'));
    };

    const canGoToPrevious = () => {
        const currentMonthStart = moment().startOf('month');
        const prevMonth = moment(currentMonth).subtract(1, 'month');
        return prevMonth.isSameOrAfter(currentMonthStart);
    };

    // Check for conflicts in current selection
    const conflicts = useMemo(() => {
        if (!unitId || !selectedCheckIn || !selectedCheckOut) return [];
        return checkBookingConflicts(unitId, selectedCheckIn, selectedCheckOut, excludeBookingId);
    }, [unitId, selectedCheckIn, selectedCheckOut, excludeBookingId]);

    const getDateStyle = (date) => {
        const slot = availability[date];
        const isSelected = date >= selectedCheckIn && date < selectedCheckOut;
        const isCheckIn = date === selectedCheckIn;
        const isCheckOut = date === selectedCheckOut;
        const isHovered = date === hoveredDate;

        if (!slot) {
            return 'bg-slate-100 text-slate-400 cursor-not-allowed';
        }

        let baseStyle = 'cursor-pointer transition-all duration-200 ';

        // Selection styles
        if (isSelected) {
            baseStyle += 'bg-blue-500 text-white shadow-md ';
        } else if (isHovered && slot.isSelectable) {
            baseStyle += 'bg-blue-100 text-blue-700 ';
        }

        // Availability styles
        switch (slot.availabilityType) {
            case 'standard':
                if (!isSelected) {
                    baseStyle += 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 ';
                }
                break;
            case 'early-checkout':
                if (!isSelected) {
                    baseStyle += 'bg-orange-50 text-orange-700 border border-orange-200 hover:bg-orange-100 ';
                }
                break;
            case 'late-checkout':
                if (!isSelected) {
                    baseStyle += 'bg-yellow-50 text-yellow-700 border border-yellow-200 hover:bg-yellow-100 ';
                }
                break;
            case 'blocked':
                baseStyle += 'bg-red-50 text-red-700 border border-red-200 cursor-not-allowed opacity-60 ';
                break;
        }

        // Special indicators
        if (isCheckIn && !isSelected) baseStyle += 'ring-2 ring-blue-400 ';
        if (isCheckOut && !isSelected) baseStyle += 'ring-2 ring-blue-400 ';

        return baseStyle;
    };

    const getDateIcon = (date) => {
        const slot = availability[date];
        if (!slot) return null;

        switch (slot.availabilityType) {
            case 'standard':
                return <CheckCircle size={12} className="text-green-600" />;
            case 'early-checkout':
                return <Clock size={12} className="text-orange-600" />;
            case 'late-checkout':
                return <Clock size={12} className="text-yellow-600" />;
            case 'blocked':
                return <AlertTriangle size={12} className="text-red-600" />;
            default:
                return null;
        }
    };

    const handleDateClick = (date) => {
        const slot = availability[date];
        if (!slot || !slot.isSelectable) return;

        if (!selectedCheckIn || (selectedCheckIn && selectedCheckOut)) {
            // Start new selection
            onDateChange(date, '');
        } else if (selectedCheckIn && !selectedCheckOut) {
            // Complete selection
            if (date > selectedCheckIn) {
                onDateChange(selectedCheckIn, date);
            } else {
                // If clicked date is before check-in, start new selection
                onDateChange(date, '');
            }
        }
    };

    const formatCalendarDate = (date) => {
        return moment(date).format('D');
    };

    const getMonthYear = (date) => {
        return moment(date).format('MMMM YYYY');
    };

    // Group dates by month
    const monthGroups = useMemo(() => {
        const groups = {};

        calendarDates.forEach(date => {
            const monthKey = date.slice(0, 7); // YYYY-MM
            if (!groups[monthKey]) {
                groups[monthKey] = [];
            }
            groups[monthKey].push(date);
        });

        return groups;
    }, [calendarDates]);

    return (
        <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-100">
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-base sm:text-lg font-semibold text-slate-900 flex items-center space-x-2 rtl:space-x-reverse">
                    <Calendar size={20} className="text-blue-600" />
                    <span className="hidden sm:inline">{t('availabilityCalendar')} - {unitNumber}</span>
                    <span className="sm:hidden">{unitNumber}</span>
                </h3>

                {/* Month Navigation */}
                <div className="flex items-center space-x-2 sm:space-x-4 rtl:space-x-reverse">
                    <button
                        onClick={() => navigateMonth('prev')}
                        disabled={!canGoToPrevious()}
                        className="p-1.5 sm:p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <ChevronLeft size={16} className="sm:w-5 sm:h-5" />
                    </button>

                    <div className="text-center min-w-[100px] sm:min-w-[140px]">
                        <h4 className="font-semibold text-slate-900 text-sm sm:text-base">
                            {currentMonth.format('MMMM YYYY')}
                        </h4>
                        <button
                            onClick={goToToday}
                            className="text-xs text-blue-600 hover:text-blue-800 transition-colors duration-200 hidden sm:block"
                        >
                            {t('now')}
                        </button>
                    </div>

                    <button
                        onClick={() => navigateMonth('next')}
                        className="p-1.5 sm:p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors duration-200"
                    >
                        <ChevronRight size={16} className="sm:w-5 sm:h-5" />
                    </button>
                </div>

                {conflicts.length > 0 && (
                    <button
                        onClick={() => setShowConflicts(!showConflicts)}
                        className="flex items-center space-x-1 sm:space-x-2 rtl:space-x-reverse px-2 sm:px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors duration-200"
                    >
                        <AlertTriangle size={14} />
                        <span className="text-xs sm:text-sm">{conflicts.length}</span>
                        <span className="text-xs sm:text-sm hidden sm:inline">{t('conflicts')}</span>
                    </button>
                )}
            </div>

            {/* Legend */}
            <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-slate-50 rounded-xl">
                <h4 className="font-medium text-slate-900 mb-2 sm:mb-3 text-sm sm:text-base">{t('availabilityLegend')}:</h4>
                <div className="grid grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm">
                    <div className="flex items-center space-x-2 rtl:space-x-reverse">
                        <div className="w-3 h-3 sm:w-4 sm:h-4 bg-green-100 border border-green-200 rounded"></div>
                        <span className="text-slate-700">{t('standardAvailable')}</span>
                    </div>
                    <div className="flex items-center space-x-2 rtl:space-x-reverse">
                        <div className="w-3 h-3 sm:w-4 sm:h-4 bg-orange-100 border border-orange-200 rounded"></div>
                        <span className="text-slate-700">{t('earlyCheckoutAvailable')}</span>
                    </div>
                    <div className="flex items-center space-x-2 rtl:space-x-reverse">
                        <div className="w-3 h-3 sm:w-4 sm:h-4 bg-yellow-100 border border-yellow-200 rounded"></div>
                        <span className="text-slate-700">{t('lateCheckoutAvailable')}</span>
                    </div>
                    <div className="flex items-center space-x-2 rtl:space-x-reverse">
                        <div className="w-3 h-3 sm:w-4 sm:h-4 bg-red-100 border border-red-200 rounded"></div>
                        <span className="text-slate-700">{t('blocked')}</span>
                    </div>
                </div>
            </div>

            {/* Calendar Grid */}
            <div className="space-y-6">
                <div className="grid grid-cols-7 gap-1 sm:gap-2">
                    {/* Day headers */}
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="text-center text-xs font-medium text-slate-500 py-1 sm:py-2">
                            {t(day)}
                        </div>
                    ))}

                    {/* Empty cells for month start */}
                    {Array.from({ length: moment(calendarDates[0]).day() }, (_, i) => (
                        <div key={`empty-${i}`} className="h-8 sm:h-10 lg:h-12"></div>
                    ))}

                    {/* Date cells */}
                    {calendarDates.map(date => {
                        const slot = availability[date];
                        const dayNumber = formatCalendarDate(date);
                        const today = moment().format('YYYY-MM-DD');
                        const isPastDate = moment(date).isBefore(today, 'day');

                        return (
                            <div
                                key={date}
                                onClick={() => !isPastDate && handleDateClick(date)}
                                onMouseEnter={() => setHoveredDate(date)}
                                onMouseLeave={() => setHoveredDate(null)}
                                className={`relative h-12 flex flex-col items-center justify-center rounded-lg text-sm font-medium ${isPastDate
                                        ? 'bg-slate-100 text-slate-400 cursor-not-allowed opacity-50'
                                        : getDateStyle(date)
                                    }`}
                                title={slot ? `${date} - ${slot.availableFrom} to ${slot.availableUntil}` : date}
                            >
                                <span className="text-xs sm:text-sm">{dayNumber}</span>
                                {slot && !isPastDate && (
                                    <div className="absolute top-0 right-0 p-0.5 hidden sm:block">
                                        {getDateIcon(date)}
                                    </div>
                                )}

                                {/* Revenue opportunity indicator */}
                                {slot?.revenueOpportunity && slot.revenueOpportunity > 0 && !isPastDate && (
                                    <div className="absolute bottom-0 left-0 right-0 hidden sm:block">
                                        <div className="bg-green-500 text-white text-xs px-1 py-0.5 rounded-b">
                                            +{slot.revenueOpportunity}
                                        </div>
                                    </div>
                                )}

                                {/* Special timing indicator */}
                                {slot && slot.availabilityType !== 'standard' && slot.availabilityType !== 'blocked' && !isPastDate && (
                                    <div className="absolute top-0 left-0 w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-blue-500"></div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Quick Month Navigation */}
            <div className="mt-3 sm:mt-4 flex flex-wrap items-center justify-center gap-2 sm:space-x-2 rtl:sm:space-x-reverse">
                <button
                    onClick={() => {
                        setCurrentMonth(moment(currentMonth).add(1, 'month'));
                    }}
                    className="px-2 sm:px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors duration-200"
                >
                    {t('nextMonth')}
                </button>

                <button
                    onClick={() => {
                        setCurrentMonth(moment(currentMonth).add(1, 'year'));
                    }}
                    className="px-2 sm:px-3 py-1 text-xs bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors duration-200"
                >
                    {t('nextYear')}
                </button>

                <button
                    onClick={goToToday}
                    className="px-2 sm:px-3 py-1 text-xs bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors duration-200"
                >
                    {t('today')}
                </button>
            </div>

            {/* Month/Year Selector */}
            <div className="mt-3 sm:mt-4 grid grid-cols-2 gap-2 sm:gap-4">
                <div>
                    <label className="hidden sm:block text-xs font-medium text-slate-700 mb-1">
                        {t('month')}
                    </label>
                    <select
                        value={currentMonth.month()}
                        onChange={(e) => {
                            setCurrentMonth(moment(currentMonth).month(parseInt(e.target.value)));
                        }}
                        className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                        {Array.from({ length: 12 }, (_, i) => (
                            <option key={i} value={i}>
                                {moment().month(i).format(window.innerWidth < 640 ? 'MMM' : 'MMMM')}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="hidden sm:block text-xs font-medium text-slate-700 mb-1">
                        {t('year')}
                    </label>
                    <select
                        value={currentMonth.year()}
                        onChange={(e) => {
                            setCurrentMonth(moment(currentMonth).year(parseInt(e.target.value)));
                        }}
                        className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                        {Array.from({ length: 5 }, (_, i) => {
                            const year = moment().year() + i;
                            return (
                                <option key={year} value={year}>
                                    {year}
                                </option>
                            );
                        })}
                    </select>
                </div>
            </div>

            {/* Selection Summary */}
            {selectedCheckIn && selectedCheckOut && (
                <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-xl">
                    <div className="flex items-center space-x-3 rtl:space-x-reverse">
                        <Calendar size={16} className="text-blue-600" />
                        <div>
                            <h4 className="font-semibold text-blue-900 text-sm sm:text-base">
                                {t('selectedPeriod')}
                            </h4>
                            <p className="text-blue-700 text-xs sm:text-sm">
                                {moment(selectedCheckIn).format('L')} - {moment(selectedCheckOut).format('L')}
                                <span className="ms-1 sm:ms-2">
                                    ({moment(selectedCheckOut).diff(moment(selectedCheckIn), 'days')} {t('nights')})
                                </span>
                            </p>
                            {conflicts.length > 0 && (
                                <div className="mt-1 sm:mt-2 flex items-center space-x-1 sm:space-x-2 rtl:space-x-reverse text-red-600">
                                    <AlertTriangle size={14} />
                                    <span className="text-xs sm:text-sm font-medium">
                                        {conflicts.length} {t('conflictsDetected')}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Conflicts Display */}
            {showConflicts && conflicts.length > 0 && (
                <div className="mt-4 sm:mt-6 space-y-3 sm:space-y-4">
                    <h4 className="font-semibold text-red-900 flex items-center space-x-2 rtl:space-x-reverse text-sm sm:text-base">
                        <AlertTriangle size={16} />
                        <span>{t('bookingConflicts')}</span>
                    </h4>

                    {conflicts.map((conflict, index) => (
                        <div key={index} className="bg-red-50 border border-red-200 rounded-xl p-3 sm:p-4">
                            <div className="flex items-start space-x-3 rtl:space-x-reverse">
                                <AlertTriangle size={16} className="text-red-600 mt-0.5" />
                                <div className="flex-1">
                                    <h5 className="font-medium text-red-900 mb-1 text-sm">
                                        {t('conflictsWith')}: {conflict.conflictingBooking.guestName}
                                    </h5>
                                    <p className="text-red-700 text-xs sm:text-sm mb-2 sm:mb-3">
                                        {moment(conflict.conflictingBooking.checkIn).format('L')} - {moment(conflict.conflictingBooking.checkOut).format('L')}
                                    </p>

                                    {conflict.suggestedAlternatives.length > 0 && (
                                        <div>
                                            <h6 className="font-medium text-slate-900 mb-2 text-xs sm:text-sm">{t('suggestedAlternatives')}:</h6>
                                            <div className="space-y-2">
                                                {conflict.suggestedAlternatives.map((alt, altIndex) => (
                                                    <button
                                                        key={altIndex}
                                                        onClick={() => alt.checkIn && alt.checkOut && onDateChange(alt.checkIn, alt.checkOut)}
                                                        className="block w-full text-left p-2 sm:p-3 bg-white border border-slate-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors duration-200"
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <div>
                                                                <div className="font-medium text-slate-900 text-xs sm:text-sm">
                                                                    {alt.checkIn && alt.checkOut && (
                                                                        <>
                                                                            {moment(alt.checkIn).format('L')} - {moment(alt.checkOut).format('L')}
                                                                        </>
                                                                    )}
                                                                </div>
                                                                <div className="text-xs text-slate-600">{alt.reason}</div>
                                                            </div>
                                                            <div className="text-blue-600">
                                                                <span className="text-xs sm:text-sm">→</span>
                                                            </div>
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Availability Details */}
            {hoveredDate && availability[hoveredDate] && (
                <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-slate-50 border border-slate-200 rounded-xl hidden sm:block">
                    <div className="flex items-start space-x-3 rtl:space-x-reverse">
                        <Info size={16} className="text-blue-600 mt-0.5" />
                        <div>
                            <h4 className="font-medium text-slate-900 mb-2 text-sm">
                                {moment(hoveredDate).format('L')} - {t('availabilityDetails')}
                            </h4>

                            <div className="space-y-1 sm:space-y-2 text-xs sm:text-sm">
                                <div className="flex justify-between">
                                    <span className="text-slate-600">{t('availableFrom')}:</span>
                                    <span className="font-medium text-slate-900">{availability[hoveredDate].availableFrom}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-slate-600">{t('availableUntil')}:</span>
                                    <span className="font-medium text-slate-900">{availability[hoveredDate].availableUntil}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-slate-600">{t('availabilityType')}:</span>
                                    <span className="font-medium text-slate-900 capitalize">
                                        {availability[hoveredDate].availabilityType.replace('-', ' ')}
                                    </span>
                                </div>

                                {availability[hoveredDate].revenueOpportunity && (
                                    <div className="flex justify-between">
                                        <span className="text-slate-600">{t('revenueOpportunity')}:</span>
                                        <span className="font-medium text-green-600">
                                            +{availability[hoveredDate].revenueOpportunity} EGP
                                        </span>
                                    </div>
                                )}

                                {availability[hoveredDate].conflictingBooking && (
                                    <div className="mt-2 sm:mt-3 p-2 sm:p-3 bg-red-50 border border-red-200 rounded-lg">
                                        <div className="font-medium text-red-900 mb-1 text-xs sm:text-sm">
                                            {t('existingBooking')}:
                                        </div>
                                        <div className="text-red-700 text-xs">
                                            {availability[hoveredDate].conflictingBooking?.guestName} -
                                            {availability[hoveredDate].conflictingBooking?.checkIn} to {availability[hoveredDate].conflictingBooking?.checkOut}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};